import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';

import { parseISO, formatISO, format, addMonths, subMonths, setDate, startOfDay } from 'date-fns';

import {compare, constants, filterSchedules, generateScheduleMap} from '@src/schedule/common';

Vue.use(Vuex);

const TODAY = startOfDay(new Date());

export const store = new Vuex.Store({
	state: {
		categories: [{
			categoryId: constants.CATEGORY_UNSELECTED,
			name: '전체',
			color: '',
		}],
		selectedCategory: constants.CATEGORY_UNSELECTED,

		isFolded: false,

		date: new Date(),
		calendarId: '',
		schedules: {},
		visited: [],
	},

	getters: {
		currentSchedules(state) {
			const schedules = filterSchedules(state.schedules, {
				year: state.date.getFullYear(),
				month: state.date.getMonth(),
				category: state.selectedCategory
			});
			const scheduleMap = generateScheduleMap(schedules);

			return Object.entries(scheduleMap)
				.filter(v => v[0].slice(0, 7) === format(state.date, 'yyyy-MM'))
				.sort((a, b) => a[0] >= b[0] ? 1 : -1);
		},

		isFirstMonth(state) { // 2017년 이전 스케줄 조회 불가
			return state.date.getFullYear() === 2017 && state.date.getMonth() === 0;
		},
		isLastMonth(state) {
			return state.date.getFullYear() === TODAY.getFullYear() + 1 && state.date.getMonth() === 11;
		},
	},

	mutations: {
		setCategories(state, categories) {
			state.categories = categories;
		},
		changeCategory(state, selected) {
			state.selectedCategory = selected;
			sessionStorage.setItem('category', selected);
		},

		setFoldStatus(state, isFolded) {
			state.isFolded = isFolded;
			sessionStorage.setItem('fold', state.isFolded);
		},
		toggleCalendar(state) {
			state.isFolded = !state.isFolded;
			sessionStorage.setItem('fold', state.isFolded);
		},

		changeDate(state, date) {
			state.date = date ? date : new Date();
			sessionStorage.setItem('date', format(state.date, 'yyyy-MM-dd'));
		},

		setCalendarId(state, calendarId) {
			state.calendarId = calendarId;
		},
		appendSchedules(state, { year, month, schedules }) {
			const key = format(new Date(year, month), 'yyyy-MM');
			state.schedules = {
				...state.schedules,
				[key]: schedules,
			};
			state.visited.push(key);
		},
	},

	actions: {
		async initCalendar({ state, dispatch }) {
			await dispatch('loadCategories');
			await dispatch('loadFold');

			await dispatch('loadDate');
			const year = state.date.getFullYear();
			const month = state.date.getMonth();

			await Promise.all([
				dispatch('loadSchedules', { year, month: month - 1 }),
				dispatch('loadSchedules', { year, month: month + 1 }),
				dispatch('loadSchedules', { year, month }),
			]);
		},

		async loadCategories({ state, commit }) {
			const { data } = await axios.get(`/${CAFEAPP.GRPCODE}/${CAFEAPP.FLDID}/schedule/categories`);
			commit('setCategories', [...state.categories, ...data.data]);

			const selected = sessionStorage.getItem('category');
			if (selected) {
				commit('changeCategory', parseInt(selected));
			}
		},
		async loadFold({ commit }) {
			commit('setFoldStatus', sessionStorage.getItem('fold') === 'true');
		},

		async loadDate({ commit }) {
			const date = sessionStorage.getItem('date');
			commit('changeDate', date && parseISO(date));
		},
		async loadSchedules({ state, commit }, { year, month }) {
			const start = new Date(year, month);
			const end = addMonths(start, 1);

			if (start.getFullYear() < 2017 || start.getFullYear() > TODAY.getFullYear() + 1) {
				return;
			}

			if (state.visited.includes(format(start, 'yyyy-MM'))) {
				return;
			}

			const { data } = (await axios.get(`/${CAFEAPP.GRPCODE}/${CAFEAPP.FLDID}/schedule`, {
				params: {
					startTime: formatISO(start),
					endTime: formatISO(end),
				},
			}));

			commit('setCalendarId', data.data.calendarId);
			commit('appendSchedules', { year: start.getFullYear(), month: start.getMonth(), schedules: data.data.scheduleList.sort(compare) });
		},
		async selectToday({ state, commit, dispatch }) {
			commit('changeDate');

			const year = state.date.getFullYear();
			const month = state.date.getMonth();

			await Promise.all([
				dispatch('loadSchedules', { year, month: month - 1 }),
				dispatch('loadSchedules', { year, month: month + 1 }),
				dispatch('loadSchedules', { year, month }),
			]);
		},

		increaseMonth({ state, getters, commit, dispatch }) {
			if (getters.isLastMonth) {
				return;
			}

			const nextMonth = addMonths(state.date, 1);
			const isCurrentMonth = TODAY.getFullYear() === nextMonth.getFullYear() && TODAY.getMonth() === nextMonth.getMonth();
			const newFocusedDate = setDate(nextMonth, isCurrentMonth ? TODAY.getDate() : 1);
			commit('changeDate', newFocusedDate);
			dispatch('loadSchedules', { year: newFocusedDate.getFullYear(), month: newFocusedDate.getMonth() + 1 });
		},
		decreaseMonth({ state, getters, commit, dispatch }) {
			if (getters.isFirstMonth) {
				return;
			}

			const prevMonth = subMonths(state.date, 1);
			const isCurrentMonth = TODAY.getFullYear() === prevMonth.getFullYear() && TODAY.getMonth() === prevMonth.getMonth();
			const newFocusedDate = setDate(prevMonth, isCurrentMonth ? TODAY.getDate() : 1);
			commit('changeDate', newFocusedDate);
			dispatch('loadSchedules', { year: newFocusedDate.getFullYear(), month: newFocusedDate.getMonth() + 1 });
		}
	}
});
